import { useAuth0 } from "@auth0/auth0-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { customClient } from "@utils/helpers";
import { useEffect, useMemo } from "react";

import {
  CaregiverType,
  CaregiversFilter,
  GetCaregiversDataDocument,
  GetCaregiversDataQuery,
} from "@api/graphql/types-and-hooks";

interface UseAllCaregiversParams {
  enabled?: boolean;
  filter?: CaregiversFilter;
}

export const useAllCaregivers = ({ enabled = true, filter }: UseAllCaregiversParams = {}) => {
  const { getAccessTokenSilently } = useAuth0();

  const query = useInfiniteQuery({
    queryKey: ["getCaregiversDataQuery", filter],
    queryFn: async ({ pageParam = { after: null } }) => {
      const token = await getAccessTokenSilently();
      const response = (await customClient({
        token,
        document: GetCaregiversDataDocument,
        variables: { after: pageParam.after, filter },
      })) as GetCaregiversDataQuery;

      return response.caregivers;
    },
    initialPageParam: { after: null as string | null | undefined },
    getNextPageParam: (lastPage) => {
      if (lastPage?.pageInfo?.hasNextPage) {
        return { after: lastPage.pageInfo.endCursor };
      }
      return undefined;
    },
    enabled,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const { hasNextPage, fetchNextPage, isFetchingNextPage } = query;

  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const caregivers: CaregiverType[] = useMemo(
    () =>
      query.data?.pages.flatMap(
        (page) => page?.edges?.map((edge) => edge?.node).filter((node): node is CaregiverType => !!node) || [],
      ) || [],
    [query.data],
  );

  return {
    caregivers,
    isLoading: query.isLoading,
    isFetchingMore: isFetchingNextPage,
    refetch: query.refetch,
  };
};
